import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Shield, ArrowRight, Clock, DollarSign, ExternalLink, 
  ChevronRight, Check, AlertCircle, FileText, Fingerprint, MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PCCPage = () => {
  const [pccCountries, setPccCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("IN");
  const [pccDetails, setPccDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPCCCountries();
    fetchPCCDetails("IN");
  }, []);

  const fetchPCCCountries = async () => {
    try {
      const response = await axios.get(`${API}/pcc`);
      setPccCountries(response.data.pcc_countries);
    } catch (error) {
      console.error("Error fetching PCC countries:", error);
    }
  };

  const fetchPCCDetails = async (countryCode) => {
    try {
      const response = await axios.get(`${API}/pcc/${countryCode}`);
      setPccDetails(response.data);
      setSelectedCountry(countryCode);
    } catch (error) {
      console.error("Error fetching PCC details:", error);
      toast.error("Failed to load PCC details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#E15636] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#64748B]">Loading PCC information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 md:pt-32 pb-20" data-testid="pcc-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-8">
        <div className="flex items-center gap-2 text-sm text-[#64748B]">
          <Link to="/" className="hover:text-[#0F172A]">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-[#0F172A] font-medium">Police Clearance Certificate</span>
        </div>
      </div>

      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-12">
        <div className="flex items-center gap-2 text-[#E15636] mb-4">
          <Shield className="w-5 h-5" />
          <span className="text-sm font-medium uppercase tracking-widest">Background Check</span>
        </div>
        <h1 className="text-4xl md:text-6xl font-heading tracking-tight text-[#0F172A] mb-4">
          Police Clearance <span className="text-[#E15636]">Certificate</span>
        </h1>
        <p className="text-lg text-[#64748B] max-w-2xl">
          Official document certifying criminal record status. Required for immigration, 
          employment, and visa applications in various countries.
        </p>
      </section>

      {/* Country Selector */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-12">
        <div className="flex gap-4">
          {pccCountries.map((country) => (
            <button
              key={country.code}
              onClick={() => fetchPCCDetails(country.code)}
              className={`flex items-center gap-3 px-6 py-4 rounded-xl border-2 transition-all ${
                selectedCountry === country.code
                  ? 'border-[#E15636] bg-[#E15636]/5'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
              data-testid={`pcc-country-${country.code}`}
            >
              <span className="text-3xl">{country.flag}</span>
              <div className="text-left">
                <h3 className="font-medium text-[#0F172A]">{country.name}</h3>
                <p className="text-xs text-[#64748B]">CAD ${country.fee_cad} • {country.processing_time}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Main Content */}
      {pccDetails && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-8">
              {/* Header Card */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8" data-testid="pcc-header">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <span className="text-5xl">{pccDetails.flag_emoji}</span>
                    <div>
                      <h2 className="text-2xl font-heading text-[#0F172A]">
                        {pccDetails.name}
                      </h2>
                      <p className="text-[#64748B]">{pccDetails.country_name}</p>
                    </div>
                  </div>
                  <a 
                    href={pccDetails.official_portal}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="rounded-full bg-[#E15636] hover:bg-[#C24125]">
                      Official Portal
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>

                <p className="text-[#64748B] mb-6">{pccDetails.description}</p>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-[#64748B] text-sm mb-1">
                      <DollarSign className="w-4 h-4" />
                      Fee
                    </div>
                    <p className="text-xl font-heading text-[#0F172A]">
                      CAD ${pccDetails.fee_cad}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-[#64748B] text-sm mb-1">
                      <Clock className="w-4 h-4" />
                      Processing
                    </div>
                    <p className="text-lg font-heading text-[#0F172A]">{pccDetails.processing_time}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-[#64748B] text-sm mb-1">
                      <Shield className="w-4 h-4" />
                      Validity
                    </div>
                    <p className="text-lg font-heading text-[#0F172A]">{pccDetails.validity}</p>
                  </div>
                </div>
              </div>

              {/* India-specific: Apply from India vs Abroad */}
              {pccDetails.country_code === "IN" && (
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <Tabs defaultValue="india" className="w-full">
                    <TabsList className="w-full justify-start border-b border-slate-200 rounded-none bg-slate-50 p-0 h-auto">
                      <TabsTrigger 
                        value="india" 
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E15636] data-[state=active]:bg-white px-6 py-4"
                      >
                        <MapPin className="w-4 h-4 mr-2" />
                        Apply from India
                      </TabsTrigger>
                      <TabsTrigger 
                        value="abroad" 
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E15636] data-[state=active]:bg-white px-6 py-4"
                      >
                        <Globe className="w-4 h-4 mr-2" />
                        Apply from Abroad
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="india" className="p-6 md:p-8 mt-0">
                      <div className="space-y-6">
                        <div>
                          <h3 className="font-heading text-lg text-[#0F172A] mb-4">Application Process</h3>
                          <div className="space-y-3">
                            {pccDetails.apply_from_india?.process?.map((step, index) => (
                              <div 
                                key={index}
                                className="flex items-start gap-4 p-3 bg-slate-50 rounded-lg"
                              >
                                <div className="w-8 h-8 bg-[#E15636] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">
                                  {index + 1}
                                </div>
                                <p className="text-[#0F172A] pt-1">{step}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h3 className="font-heading text-lg text-[#0F172A] mb-4">Required Documents</h3>
                          <div className="space-y-3">
                            {pccDetails.apply_from_india?.documents?.map((doc, index) => (
                              <div 
                                key={index}
                                className={`flex items-start gap-3 p-3 bg-slate-50 rounded-lg ${
                                  doc.mandatory ? 'border-l-4 border-l-[#E15636]' : ''
                                }`}
                              >
                                <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                                  doc.mandatory ? 'text-[#E15636]' : 'text-slate-400'
                                }`} />
                                <div>
                                  <h4 className="font-medium text-[#0F172A]">{doc.name}</h4>
                                  <p className="text-sm text-[#64748B]">{doc.description}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <a 
                          href={pccDetails.apply_from_india?.portal}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <Button className="w-full rounded-full bg-[#0F172A] hover:bg-[#1E293B]">
                            Passport Seva Portal
                            <ExternalLink className="w-4 h-4 ml-2" />
                          </Button>
                        </a>
                      </div>
                    </TabsContent>

                    <TabsContent value="abroad" className="p-6 md:p-8 mt-0">
                      <div className="space-y-6">
                        <div>
                          <h3 className="font-heading text-lg text-[#0F172A] mb-4">Application Process (from Canada)</h3>
                          <div className="space-y-3">
                            {pccDetails.apply_from_abroad?.process?.map((step, index) => (
                              <div 
                                key={index}
                                className="flex items-start gap-4 p-3 bg-slate-50 rounded-lg"
                              >
                                <div className="w-8 h-8 bg-[#E15636] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">
                                  {index + 1}
                                </div>
                                <p className="text-[#0F172A] pt-1">{step}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h3 className="font-heading text-lg text-[#0F172A] mb-4">Required Documents</h3>
                          <div className="space-y-3">
                            {pccDetails.apply_from_abroad?.documents?.map((doc, index) => (
                              <div 
                                key={index}
                                className={`flex items-start gap-3 p-3 bg-slate-50 rounded-lg ${
                                  doc.mandatory ? 'border-l-4 border-l-[#E15636]' : ''
                                }`}
                              >
                                <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                                  doc.mandatory ? 'text-[#E15636]' : 'text-slate-400'
                                }`} />
                                <div>
                                  <h4 className="font-medium text-[#0F172A]">{doc.name}</h4>
                                  <p className="text-sm text-[#64748B]">{doc.description}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <h4 className="font-medium text-blue-800 mb-2">Submission Centers in Canada</h4>
                          <p className="text-sm text-blue-700">
                            {pccDetails.apply_from_abroad?.canada_centers?.join(" • ")}
                          </p>
                        </div>

                        <a 
                          href={pccDetails.apply_from_abroad?.portal}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <Button className="w-full rounded-full bg-[#0F172A] hover:bg-[#1E293B]">
                            BLS India Canada Portal
                            <ExternalLink className="w-4 h-4 ml-2" />
                          </Button>
                        </a>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              )}

              {/* Canada-specific: Types and Process */}
              {pccDetails.country_code === "CA" && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 space-y-8">
                  {/* Types */}
                  <div>
                    <h3 className="font-heading text-lg text-[#0F172A] mb-4">Types of Criminal Record Checks</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {pccDetails.types?.map((type, index) => (
                        <div 
                          key={index}
                          className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                        >
                          <h4 className="font-medium text-[#0F172A] mb-2">{type.name}</h4>
                          <p className="text-sm text-[#64748B] mb-3">{type.description}</p>
                          <Badge className="bg-emerald-100 text-emerald-700">
                            CAD ${type.fee_cad}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Process */}
                  <div>
                    <h3 className="font-heading text-lg text-[#0F172A] mb-4">Application Process</h3>
                    <div className="space-y-3">
                      {pccDetails.apply_process?.map((step, index) => (
                        <div 
                          key={index}
                          className="flex items-start gap-4 p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="w-8 h-8 bg-[#E15636] text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">
                            {index + 1}
                          </div>
                          <p className="text-[#0F172A] pt-1">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Documents */}
                  <div>
                    <h3 className="font-heading text-lg text-[#0F172A] mb-4">Required Documents</h3>
                    <div className="space-y-3">
                      {pccDetails.documents_required?.map((doc, index) => (
                        <div 
                          key={index}
                          className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border-l-4 border-l-[#E15636]"
                        >
                          <Check className="w-5 h-5 text-[#E15636] flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-[#0F172A]">{doc.name}</h4>
                            <p className="text-sm text-[#64748B]">{doc.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Quick Links & Notes */}
            <div className="space-y-8">
              {/* Official Portal */}
              <div className="bg-[#0F172A] rounded-2xl p-6 text-white">
                <h3 className="text-lg font-heading mb-4">Apply Now</h3>
                <p className="text-slate-300 text-sm mb-4">
                  Apply through the official government portal
                </p>
                <a 
                  href={pccDetails.official_portal}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full rounded-full bg-[#E15636] hover:bg-[#C24125]">
                    Official Portal
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>

              {/* Fingerprint Locations (Canada) */}
              {pccDetails.fingerprint_locations && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="fingerprint-locations">
                  <h3 className="text-lg font-heading text-[#0F172A] mb-4 flex items-center gap-2">
                    <Fingerprint className="w-5 h-5 text-[#E15636]" />
                    Fingerprint Locations
                  </h3>
                  <div className="space-y-3">
                    {pccDetails.fingerprint_locations.map((location, index) => (
                      <a
                        key={index}
                        href={location.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                      >
                        <span className="text-[#0F172A]">{location.name}</span>
                        <ExternalLink className="w-4 h-4 text-[#64748B]" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                <h3 className="font-heading text-lg text-amber-800 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Important Notes
                </h3>
                <ul className="space-y-2">
                  {pccDetails.notes?.map((note, index) => (
                    <li key={index} className="text-sm text-amber-700 flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      {note}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Related Services */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="text-lg font-heading text-[#0F172A] mb-4">Related Services</h3>
                <div className="space-y-3">
                  <Link 
                    to="/oci"
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    <span className="text-[#0F172A]">OCI Card Services</span>
                    <ArrowRight className="w-4 h-4 text-[#64748B]" />
                  </Link>
                  <Link 
                    to="/visa-finder"
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    <span className="text-[#0F172A]">Visa Requirements</span>
                    <ArrowRight className="w-4 h-4 text-[#64748B]" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

// Globe icon component (missing from lucide import)
const Globe = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

export default PCCPage;
